import React, { useEffect, useMemo, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    FormControl,
    Autocomplete,
    Typography,
} from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useNotification } from 'context/NotificationContext';
import styles from "./styles.module.scss"
import { useCreateJourneyMutation } from 'pages/TestJourneys/queries/useJourneysQuery';
import { useProject } from 'context/ProjectContext';
import { usePagesListQuery, useProjectSiteMap } from './queries/usePagesQuery';
import { sortPagesWithProjectUrlFirst } from 'utils/urlUtils';
import AddPageModal from 'components/modals/AddPageModal';
import { useQueryClient } from '@tanstack/react-query';
import { pagesQueryKeys } from './queries/keys';

const addNewId = 'AddNewPage';

const validationSchema = Yup.object({
    pageId: Yup.string().required("Url is a required field"),
    name: Yup.string().required().min(5).max(50),
});

const CreateJourneyModal = ({ open, onClose }) => {
    const { selectedProject } = useProject()
    const selectedProjectId = selectedProject?.id
    const showNotification = useNotification()
    const [nameError, setNameError] = useState('')

    const { data: pages = [], isLoading: isPagesLoading } = usePagesListQuery(selectedProjectId);

    const onSuccess = (createdJourney) => {
        setNameError('')
        onClose({ isSuccess: true, createdJourney });
    }

    const handleCancel = () => {
        setNameError('')
        onClose({ isSuccess: false });
    }

    const onError = (error) => {
        const errorMessage = error?.response?.data?.error || error?.response?.data?.message || error?.message || "Error! Something went wrong, please try again later.";
        if (errorMessage.toLowerCase().includes("already exists")) {
            setNameError(errorMessage);
        } else {
            showNotification(errorMessage, "error", "", null);
            onClose({ isSuccess: false });
        }
    }

    const { mutateAsync: createJourneyAsync, isLoading: isCreatingJourney } = useCreateJourneyMutation({ projectId: selectedProjectId, onSuccess, onError });

    const handleSubmit = async (values, { setSubmitting }) => {
        setNameError('')
        const { pageId, name } = values;
        try {
            await createJourneyAsync({ pageId, name });
        } catch (error) {
            if (setSubmitting) {
                setSubmitting(false);
            }
        }
    }

    /**
     * start - add page modal logic
     * */
    const queryClient = useQueryClient();
    const { data: sitemap = [] } = useProjectSiteMap(selectedProjectId);

    const [pageModalOpen, setPageOpenModal] = useState(false);
    const [pageUrl, setPageUrl] = useState('');

    const sortedPages = useMemo(() => {
        return sortPagesWithProjectUrlFirst(pages, selectedProject?.url);
    }, [pages, selectedProject?.url]);

    useEffect(() => {
        queryClient.invalidateQueries([pagesQueryKeys.pagesList, pagesQueryKeys.siteMapList])
    }, [pageModalOpen, queryClient])

    useEffect(() => {
        if (!open) {
            setNameError('')
        }
    }, [open])

    /**
     * end - add page modal logic
     * */

    return (
        <>
            <Dialog className={styles.createJourneyContent} open={open} onClose={handleCancel} fullWidth>
                <DialogTitle className={styles.title}>
                    Create Test Journey
                </DialogTitle>

                <Formik
                    initialValues={{ pageId: '', name: '' }}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ values, errors, touched, handleChange, handleBlur, isSubmitting, setFieldValue, setFieldTouched, submitForm }) => (
                        <Form>
                            <DialogContent className={styles.formContent}>
                                <FormControl
                                    fullWidth
                                    margin="dense"
                                    error={touched.duration && Boolean(errors.duration)}
                                >
                                    <Typography>Please enter URL of start point of the test journey</Typography>
                                    <Autocomplete
                                        className={styles.autoComplete}
                                        loading={isPagesLoading}
                                        options={[{ url: 'Add new page', id: addNewId }, ...sortedPages]}
                                        getOptionLabel={(option) => option.url}
                                        onChange={(_, item) => setFieldValue("pageId", item?.id || '')}
                                        onBlur={() => {
                                            setFieldTouched("pageId", true);
                                        }}
                                        renderOption={(props, option) => {
                                            const key = props.key;
                                            delete props.key;
                                            if (option.id === addNewId) {
                                                return (
                                                    <li
                                                        key={key}
                                                        {...props}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            setPageOpenModal(true);
                                                        }}
                                                        style={{ color: "#1976d2", fontWeight: 500 }}
                                                    >
                                                        ➕ {option.url}
                                                    </li>
                                                );
                                            }
                                            return <li key={key} {...props}>{option.url}</li>
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                required
                                                name="pageId"
                                                label="Test journey URL"
                                                variant="outlined"
                                                error={touched.pageId && Boolean(errors.pageId)}
                                                helperText={touched.pageId && errors.pageId}
                                            />
                                        )}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                    />
                                    <Box className={styles.textInputWrapper}>
                                        <Typography>Please enter the name of test journey</Typography>
                                        <TextField
                                            fullWidth
                                            label="Test journey name"
                                            name="name"
                                            margin="dense"
                                            value={values.name}
                                            onChange={(e) => {
                                                handleChange(e);
                                                if (nameError) {
                                                    setNameError('');
                                                }
                                            }}
                                            onBlur={handleBlur}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && !e.shiftKey) {
                                                    e.preventDefault();
                                                    submitForm();
                                                }
                                            }}
                                            error={(touched.name && Boolean(errors.name)) || Boolean(nameError)}
                                            helperText={nameError || (touched.name && errors.name)}
                                        />
                                    </Box>
                                </FormControl>
                            </DialogContent>

                            <DialogActions className={styles.actionWrapper}>
                                <Button color="secondary" className={styles.button} disabled={isSubmitting || isCreatingJourney} onClick={handleCancel} type="button" variant="outlined">
                                    Cancel
                                </Button>
                                <Button color="secondary" className={styles.button} disabled={isSubmitting || isCreatingJourney} type="submit" variant="contained">
                                    {(isSubmitting || isCreatingJourney) ? 'Starting...' : 'Start'}
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>
            <AddPageModal
                projectID={selectedProject ? selectedProject.id : null}
                pages={pages}
                addPageModalOpen={pageModalOpen}
                setAddPageModalOpen={setPageOpenModal}
                sitemap={sitemap}
                pageURL={pageUrl}
                setPageURL={setPageUrl}
                //props which are not using, pass to prevent errors
                pagesToggler={false}
                setPagesToggler={() => { }}
            />
        </>
    );
};

export default CreateJourneyModal;
